import { types as api } from '@mesa-labs/mesa-api';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Pagination, PaginationDropdown, Table, Typography,
  utils,
} from '@mesa-labs/mesa-ui';
import { ColumnProp } from '@mesa-labs/mesa-ui/dist/components/Table';
import { DateTime } from 'luxon';

import { DefaultPaginationDropdownLimits } from '@mesa-labs/mesa-ui/dist/components/Pagination';
import {
  asExportFilter,
  useGetInvoiceApprovalReasonSummaryAsFacilitatorQuery,
  useGetAllInvoiceApprovalsAsFacilitatorQuery,
  useLazyExportAllInvoiceApprovalsAsFacilitatorQuery,
} from '../../redux/api/facilitators';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
  updateLimit, updatePage, updateTotalPages, updateSortField, updateSortDirection,
} from '../../redux/slices/invoiceApprovals';
import { PaginationContainer } from '../common/Pagination';
import { Header } from '../common/Header';
import { CardContainer, CardTitle, CardTitleContainer, CardsContainer, CardText, CardTitleLine } from '../common/Cards';

const InvoiceApprovalsPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const InvoiceApprovalColumns: ColumnProp[] = [
  {
    key: 'clientName',
    sortField: 'clientName',
    label: 'Client',
    width: '13%',
  },
  {
    key: 'vendorName',
    sortField: 'vendorName',
    label: 'Supplier',
    width: '13%',
  },
  {
    key: 'invoiceNumber',
    sortField: 'invoiceNumber',
    label: 'Invoice Number',
    width: '13%',
  },
  {
    key: 'invoiceDate',
    sortField: 'invoiceDate',
    label: 'Invoice Date',
    type: 'date',
    width: '13%',
  },
  {
    key: 'dueDate',
    sortField: 'dueDate',
    label: 'Due Date',
    type: 'date',
    width: '13%',
  },
  {
    key: 'totalOpenAmount',
    sortField: 'totalOpenAmount',
    label: 'Amount',
    type: 'currency',
    width: '13%',
  },
  {
    key: 'approvalReason',
    sortField: 'approvalReason',
    label: 'Issue',
    // fix color, add 2nd pill (2nd reason)
    type: 'status',
    width: '22%',
  }
];

const AllApprovalReasons = [api.ManualApprovalReason.INVALID_INVOICE_DATE, api.ManualApprovalReason.INVALID_DUE_DATE, api.ManualApprovalReason.AMOUNT_EXCEEDS_THRESHOLD];

function InvoiceApprovals(): React.ReactElement {
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const partnerId = useSelector((state) => state.auth.partnerId);
  const approvalReason = useSelector((state) => state.invoiceApprovals.approvalReason);
  const page = useSelector((state) => state.invoiceApprovals.page) || 1;
  const totalPages = useSelector((state) => state.invoiceApprovals.totalPages);
  const limit = useSelector((state) => state.invoiceApprovals.limit) || DefaultPaginationDropdownLimits[0];
  const sortField = useSelector((state) => state.invoiceApprovals.sortField);
  const sortDirection = useSelector((state) => state.invoiceApprovals.sortDirection);

  const {
    data: {
      allStates: invoiceApprovalReasonSummaries = []
    } = {}
  } = useGetInvoiceApprovalReasonSummaryAsFacilitatorQuery({ accessToken: accessToken!, partnerId: partnerId! }, { skip: !partnerId });

  const filter = useMemo(() => ({
    approvalReasons: approvalReason || AllApprovalReasons,
    sortField,
    sortDirection,
    limit: (typeof limit === 'number' ? limit : limit.value),
    page,
  }), [approvalReason, sortField, sortDirection, limit, page]);

  const {
    data: {
      data: invoiceApprovals = [],
      total: totalInvoiceApprovals,
    } = {},
    isFetching,
  } = useGetAllInvoiceApprovalsAsFacilitatorQuery({ accessToken: accessToken!, partnerId: partnerId!, ...filter }, { skip: !partnerId });

  const [exportAllInvoiceApprovalsAsFacilitator] = useLazyExportAllInvoiceApprovalsAsFacilitatorQuery();

  const invalidDueDateSummary = useMemo(() => invoiceApprovalReasonSummaries.find(s => s.reason === api.ManualApprovalReason.INVALID_DUE_DATE), [invoiceApprovalReasonSummaries]);
  const invalidInvoiceDateSummary = useMemo(() => invoiceApprovalReasonSummaries.find(s => s.reason === api.ManualApprovalReason.INVALID_INVOICE_DATE), [invoiceApprovalReasonSummaries]);
  const amountExceedsThresholdSummary = useMemo(() => invoiceApprovalReasonSummaries.find(s => s.reason === api.ManualApprovalReason.AMOUNT_EXCEEDS_THRESHOLD), [invoiceApprovalReasonSummaries]);

  const rows = useMemo(() => invoiceApprovals.map((invoiceApproval) => ({
    id: invoiceApproval.id,
    clientName: invoiceApproval.clientName,
    vendorName: invoiceApproval.vendorName,
    invoiceNumber: invoiceApproval.invoiceNumber,
    invoiceDate: DateTime.fromISO(invoiceApproval.invoiceDate),
    dueDate: invoiceApproval.dueDate ? DateTime.fromISO(invoiceApproval.dueDate) : undefined,
    totalOpenAmount: utils.formatCurrency(invoiceApproval?.totalOpenAmount || 0, 'USD'),
    approvalReason: invoiceApproval.approvalReasons
      ? approvalReason
        ? invoiceApproval.approvalReasons.find(ar => ar === approvalReason)
        : AllApprovalReasons.find(ar => invoiceApproval.approvalReasons?.includes(ar))  // find first by AllApprovalReasons ordering
      : undefined
  })), [invoiceApprovals]);

  useEffect(() => {
    if (totalInvoiceApprovals !== undefined) {
      dispatch(updateTotalPages(Math.ceil(totalInvoiceApprovals / (typeof limit === 'number' ? limit : limit.value))));
    }
  }, [limit, totalInvoiceApprovals]);

  const handleExport = async () => {
    const { data } = await exportAllInvoiceApprovalsAsFacilitator({ partnerId: partnerId!, ...asExportFilter(filter) });
    return data;
  }

  return (
    <InvoiceApprovalsPageContainer>
      <Header>Invoices Requiring Attention</Header>

      <CardsContainer>
        <CardContainer>
          <CardTitleContainer>
            <CardTitleLine color="#12B76A" />
            <CardTitle>Invalid Invoice Date</CardTitle>
          </CardTitleContainer>
          <CardText>{utils.formatCurrency(invalidInvoiceDateSummary?.sumOfOpenAmount || 0, 'USD')}</CardText>
        </CardContainer>

        <CardContainer>
          <CardTitleContainer>
            <CardTitleLine color="#335CFF" />
            <CardTitle>Invalid Due Date</CardTitle>
          </CardTitleContainer>
          <CardText>{utils.formatCurrency(invalidDueDateSummary?.sumOfOpenAmount || 0, 'USD')}</CardText>
        </CardContainer>

        <CardContainer>
          <CardTitleContainer>
            <CardTitleLine color="#335CFF" />
            <CardTitle>Exceeds Threshold</CardTitle>
          </CardTitleContainer>
          <CardText>{utils.formatCurrency(amountExceedsThresholdSummary?.sumOfOpenAmount || 0, 'USD')}</CardText>
        </CardContainer>
      </CardsContainer>

      <Table
        isLoading={isFetching}
        columns={InvoiceApprovalColumns}
        rows={rows}
        currentSortColumn={sortField}
        currentSortDirection={sortDirection}
        setSortField={(field: string) => dispatch(updateSortField(field))}
        setSortDirection={(direction: api.SortDirection) => dispatch(updateSortDirection(direction))}
        paginationComponent={totalInvoiceApprovals && (
          <PaginationContainer>
            <PaginationDropdown
              selectedLimit={(typeof limit === 'number' ? DefaultPaginationDropdownLimits.find((l) => l.value === limit) || DefaultPaginationDropdownLimits[0] : limit)}
              onChange={(item) => {
                dispatch(updateLimit(item));
                dispatch(updatePage(1));
              }}
            />
            <Typography.BodySmallBold>
              Total of
              {' '}
              {totalInvoiceApprovals !== undefined ? totalInvoiceApprovals : '...'}
              {' '}
              Invoice(s)
            </Typography.BodySmallBold>
            <Pagination
              currentPage={page}
              totalPages={totalPages || 0}
              onPrev={() => dispatch(updatePage(page - 1))}
              onNext={() => dispatch(updatePage(page + 1))}
            />
          </PaginationContainer>
        ) || (<></>)}
        customStyles={{
          columnsPadding: '24px',
          columnsBorderRadius: 0,
          paginationPadding: '0px',
          rowPadding: '0px 24px',
          columnColor: '#717680',
          columnFontSize: '12px',
          columnFontWeight: 600,
        }}
      />
    </InvoiceApprovalsPageContainer>
  );
}

export default InvoiceApprovals;